import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AuthUser } from "@/types";

const ALPHA_MIMES = new Set(["image/png", "image/webp"]);

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });
}

function resizeImageToDataUrl(src: string, mime: string, hasAlpha: boolean): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 128;
      canvas.height = 128;
      const ctx = canvas.getContext("2d");
      if (!ctx) { reject(new Error("No canvas context")); return; }
      if (!hasAlpha) {
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, 128, 128);
      }
      const side = Math.min(img.width, img.height);
      ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, 128, 128);
      resolve(canvas.toDataURL(mime, hasAlpha ? undefined : 0.85));
    };
    img.src = src;
  });
}

const FUNCTION_LABELS: Record<string, string> = {
  screen: "Resume Screening",
  intake: "Intake Analysis",
  salary: "Salary Benchmarking",
  format: "Resume Formatting",
  pipeline: "Pipeline (full)",
};

export function AccountSettings({ authUser, onProfileUpdate }: { authUser: AuthUser; onProfileUpdate: (user: Partial<AuthUser>) => void }) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data } = useQuery({
    queryKey: ["profile"],
    queryFn: () => api.getProfile(),
  });
  const profile = data?.profile;

  const { data: billingData } = useQuery({
    queryKey: ["billing-usage"],
    queryFn: () => api.getBillingUsage(),
    staleTime: 60_000,
  });
  const billing = billingData;

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [pendingAvatarUrl, setPendingAvatarUrl] = useState<string | null>(null);
  const [success, setSuccess] = useState("");
  const [initialized, setInitialized] = useState(false);

  // Initialize form when profile loads
  if (profile && !initialized) {
    setDisplayName(profile.display_name || "");
    setEmail(profile.email || "");
    setAvatarPreview(profile.avatar || null);
    setInitialized(true);
  }

  const mutation = useMutation({
    mutationFn: (body: { display_name?: string; email?: string; avatar?: string; password?: string }) =>
      api.updateProfile(body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      setSuccess("Profile updated successfully");
      setPassword("");
      setConfirmPassword("");
      onProfileUpdate({
        display_name: data.profile.display_name,
        email: data.profile.email,
        avatar: data.profile.avatar,
      });
      setTimeout(() => setSuccess(""), 3000);
    },
  });

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) {
      alert("Image must be under 512KB");
      return;
    }
    const mime = ALPHA_MIMES.has(file.type) ? file.type : "image/jpeg";
    const hasAlpha = ALPHA_MIMES.has(mime);
    const src = await readFileAsDataUrl(file);
    const dataUrl = await resizeImageToDataUrl(src, mime, hasAlpha);
    setAvatarPreview(dataUrl);
    api.uploadImage({ imageData: dataUrl, mime, slot: `avatar-${authUser.id}` })
      .then(({ url }) => setPendingAvatarUrl(url))
      .catch(() => {/* non-critical — falls back to data URL */});
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess("");

    if (password && password !== confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    const body: { display_name?: string; email?: string; avatar?: string; password?: string } = {};
    if (displayName !== (profile?.display_name || "")) body.display_name = displayName;
    if (email !== (profile?.email || "")) body.email = email;
    if (pendingAvatarUrl !== null) body.avatar = pendingAvatarUrl;
    else if (avatarPreview !== (profile?.avatar || null)) body.avatar = avatarPreview || "";
    if (password) body.password = password;

    if (Object.keys(body).length === 0) {
      setSuccess("No changes to save");
      setTimeout(() => setSuccess(""), 2000);
      return;
    }

    mutation.mutate(body);
  };

  const currentAvatar = avatarPreview || profile?.avatar;
  const nameDisplay = displayName || profile?.display_name || authUser.username;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Sticky header */}
      <div className="shrink-0 shadow-sm bg-white px-3 py-4 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="flex items-center gap-2">
            <h2 className="text-[28px] font-bold leading-[1.43] text-near-black">
              Account Settings
            </h2>
          </div>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl px-3 py-4 sm:px-6 sm:py-6">
      <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6 lg:grid-cols-[auto_1fr]">
        {/* Left column — Avatar card */}
        <div className="lg:w-64">
          <div className="rounded-card bg-white p-5 shadow-card">
            <div className="flex flex-col items-center text-center">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="group relative h-20 w-20 shrink-0 rounded-full overflow-hidden cursor-pointer"
              >
                {currentAvatar ? (
                  <img src={currentAvatar} alt={nameDisplay} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-near-black text-2xl font-bold text-white">
                    {nameDisplay[0].toUpperCase()}
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-white/0 group-hover:bg-white/40 dark:bg-black/0 dark:group-hover:bg-black/40 transition-colors rounded-full">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-black/0 group-hover:bg-black/60 dark:group-hover:bg-white/60 transition-colors">
                  <svg className="h-5 w-5 text-white dark:text-near-black opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z" />
                  </svg>
                  </div>
                </div>
              </button>
              <p className="mt-3 text-base font-bold text-near-black">{nameDisplay}</p>
              <p className="text-xs text-text-secondary">@{authUser.username}</p>
              <span className="mt-1.5 inline-flex rounded-badge bg-surface-secondary px-2.5 py-0.5 text-[11px] font-semibold text-text-secondary">
                {authUser.role_label}
              </span>
              {currentAvatar && (
                <button
                  type="button"
                  onClick={() => setAvatarPreview("")}
                  className="mt-2 text-xs font-medium text-text-secondary transition-colors hover:text-error-text"
                >
                  Remove photo
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              className="hidden"
              onChange={handleAvatarChange}
            />
          </div>
        </div>

        {/* Right column — Personal info + Password + Save */}
        <div className="space-y-6">
          {/* Personal info */}
          <div className="rounded-card bg-white p-4 shadow-card">
            <div className="text-[8px] font-bold uppercase tracking-[0.32px] text-text-secondary mb-3">
              Personal Information
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">
                  Display Name
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={authUser.username}
                  className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-1.5 text-sm text-near-black focus:outline-none focus:shadow-focus"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-1.5 text-sm text-near-black focus:outline-none focus:shadow-focus"
                />
              </div>
            </div>
          </div>
          {/* Change password */}
          <div className="rounded-card bg-white p-4 shadow-card">
            <div className="text-[8px] font-bold uppercase tracking-[0.32px] text-text-secondary mb-3">
              Change Password
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">
                  New Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Leave blank to keep current"
                  minLength={4}
                  className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-1.5 text-sm text-near-black focus:outline-none focus:shadow-focus"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-1.5 text-sm text-near-black focus:outline-none focus:shadow-focus"
                />
              </div>
            </div>
          </div>

          {/* Token Usage */}
          {billing && (
            <div className="rounded-card bg-white p-4 shadow-card">
              <div className="text-[8px] font-bold uppercase tracking-[0.32px] text-text-secondary mb-3">
                AI Token Usage — {billing.month}
              </div>
              <div className="mb-3">
                <div className="flex items-end justify-between mb-1">
                  <span className="text-sm font-semibold text-near-black">
                    {billing.credits_used.toLocaleString()} <span className="text-text-secondary font-normal">/ {billing.monthly_limit.toLocaleString()} credits</span>
                  </span>
                  {billing.is_over_limit && (
                    <span className="text-xs font-semibold text-rausch">
                      +{billing.overage_credits.toLocaleString()} cr overage · ${billing.overage_cost_usd.toFixed(2)}
                    </span>
                  )}
                </div>
                <div className="h-2 w-full rounded-full bg-surface-secondary overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${billing.is_over_limit ? "bg-rausch" : "bg-emerald-500"}`}
                    style={{ width: `${Math.min(100, (billing.credits_used / billing.monthly_limit) * 100)}%` }}
                  />
                </div>
                <p className="mt-1 text-[10px] text-text-secondary">
                  {billing.total_tokens.toLocaleString()} raw tokens · 1 credit = 100 tokens
                </p>
              </div>
              {billing.by_function.length > 0 && (
                <div className="space-y-1.5">
                  {billing.by_function.map((row) => (
                    <div key={row.function_name} className="flex items-center justify-between text-xs">
                      <span className="text-text-secondary">{FUNCTION_LABELS[row.function_name] ?? row.function_name}</span>
                      <span className="font-medium text-near-black">
                        {row.credits.toLocaleString()} cr · {row.call_count}×
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          {success && (
            <p className="rounded-badge bg-emerald-100 px-3 py-2 text-xs font-medium text-emerald-800">
              {success}
            </p>
          )}
          {mutation.isError && (
            <p className="rounded-badge bg-rose-100 px-3 py-2 text-xs font-medium text-error-text">
              {(mutation.error as Error).message}
            </p>
          )}
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded-btn bg-near-black px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-rausch disabled:opacity-50"
          >
            {mutation.isPending ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
        </div>
      </div>
    </div>
  );
}
